import { useState } from 'react';
import { Link } from 'react-router-dom';

const CORE_API = import.meta.env.VITE_CORE_API_URL || '';

export default function NominateProspect() {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<{ support_url?: string } | null>(null);
  const [err, setErr] = useState('');

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErr('');
    setSuccess(null);
    setSubmitting(true);

    const data = Object.fromEntries(new FormData(e.currentTarget));
    try {
      const res = await fetch(`${CORE_API}/api/v1/nominations/prospect`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(data),
      });
      const json = await res.json();
      if (!res.ok) {
        setErr(json.error?.message || json.error || 'Failed to submit');
        return;
      }
      setSuccess(json.data);
      e.currentTarget.reset();
    } catch {
      setErr('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-10">
      <Link to="/nominate" className="text-xs text-sky-600 hover:underline mb-4 block">&larr; Back to nominations</Link>

      <h1 className="text-3xl font-bold text-cocoa mb-2">Nominate a Prospect</h1>
      <p className="mb-6 text-cocoa/70">
        As a coach, you're nominating a young athlete for locker room support.
        If they're selected in a draw, you'll be the adult guardian managing their locker room and resources.
      </p>

      <div className="rounded-xl border border-sky-200 bg-sky-50/50 p-4 mb-6">
        <h3 className="text-sm font-semibold text-sky-800">Coach Governance</h3>
        <p className="text-xs text-sky-700 mt-1">
          As the nominating coach, you take on governance of the locker room.
          This means managing the budget, resources, and any additional prospects you nominate.
          All drawn prospects under your coaching go to the same locker room.
        </p>
      </div>

      {success ? (
        <div className="rounded-xl border border-green-200 bg-green-50 p-6 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
            <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-lg font-bold text-green-800">Nomination Submitted!</h2>
          <p className="mt-2 text-sm text-green-700">
            We'll send you a confirmation email. Once confirmed, your prospect enters the draw pool.
          </p>
          {success.support_url && (
            <div className="mt-4 rounded-lg bg-white p-3 border border-green-200">
              <p className="text-xs text-cocoa/60 mb-1">Share this link to rally support:</p>
              <code className="text-sm font-mono text-sky-700 break-all">{success.support_url}</code>
            </div>
          )}
          <Link to="/nominate" className="mt-4 inline-block text-sm text-sky-600 hover:underline">
            Nominate another
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <h3 className="text-sm font-semibold text-cocoa/60 uppercase tracking-wide">Your Info (Coach)</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-cocoa/70 mb-1">Your Name *</label>
              <input name="coach_name" className="w-full rounded-xl border border-sand bg-white px-3 py-2 text-sm text-cocoa focus:outline-none focus:ring-2 focus:ring-sky-400" required />
            </div>
            <div>
              <label className="block text-xs font-medium text-cocoa/70 mb-1">Your Email *</label>
              <input name="coach_email" type="email" className="w-full rounded-xl border border-sand bg-white px-3 py-2 text-sm text-cocoa focus:outline-none focus:ring-2 focus:ring-sky-400" required />
            </div>
          </div>

          <hr className="border-sand/50" />

          <h3 className="text-sm font-semibold text-cocoa/60 uppercase tracking-wide">Prospect Info</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-cocoa/70 mb-1">Prospect Name *</label>
              <input name="prospect_name" className="w-full rounded-xl border border-sand bg-white px-3 py-2 text-sm text-cocoa focus:outline-none focus:ring-2 focus:ring-sky-400" required />
            </div>
            <div>
              <label className="block text-xs font-medium text-cocoa/70 mb-1">Prospect Email</label>
              <input name="prospect_email" type="email" className="w-full rounded-xl border border-sand bg-white px-3 py-2 text-sm text-cocoa focus:outline-none focus:ring-2 focus:ring-sky-400" placeholder="Confirmation sent here" />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-cocoa/70 mb-1">Age</label>
              <input name="prospect_age" type="number" min="5" max="25" className="w-full rounded-xl border border-sand bg-white px-3 py-2 text-sm text-cocoa focus:outline-none focus:ring-2 focus:ring-sky-400" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-cocoa/70 mb-1">Sport / Activity</label>
            <input name="sport" className="w-full rounded-xl border border-sand bg-white px-3 py-2 text-sm text-cocoa focus:outline-none focus:ring-2 focus:ring-sky-400" placeholder="e.g. Basketball, Track & Field, Soccer" />
          </div>
          <div>
            <label className="block text-xs font-medium text-cocoa/70 mb-1">Why are you nominating them?</label>
            <textarea name="reason" rows={3} className="w-full rounded-xl border border-sand bg-white px-3 py-2 text-sm text-cocoa resize-none focus:outline-none focus:ring-2 focus:ring-sky-400" placeholder="Tell us about this athlete..." />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-xl bg-sky-500 py-3 text-sm font-semibold text-white transition hover:bg-sky-600 disabled:opacity-50"
          >
            {submitting ? 'Submitting...' : 'Submit Nomination'}
          </button>

          {err && <p className="text-sm text-red-600 text-center">{err}</p>}
        </form>
      )}
    </div>
  );
}
